import { useCallback, useEffect, useState, type RefObject } from 'react'

export function useMediaQuery(query: string): boolean {
  const read = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false)
  const [matches, setMatches] = useState(read)
  useEffect(() => {
    const mql = window.matchMedia(query)
    const onChange = () => setMatches(mql.matches)
    onChange()
    mql.addEventListener?.('change', onChange)
    return () => mql.removeEventListener?.('change', onChange)
  }, [query])
  return matches
}

/** Close a popover on an outside pointer-down or Escape. */
export function useDismiss(ref: RefObject<HTMLElement | null>, open: boolean, onDismiss: () => void): void {
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onDismiss()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss()
    }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [ref, open, onDismiss])
}

/** Re-render every `ms` so relative times ("saved 2m ago") stay honest. */
export function useNow(ms = 30_000): Date {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), ms)
    return () => clearInterval(t)
  }, [ms])
  return now
}

/** An on/off preference remembered per browser (on when never set, if `fallback` is true). */
export function useStoredToggle(key: string, fallback: boolean): [boolean, (on: boolean) => void] {
  const [on, setOn] = useState(() => {
    try {
      const v = window.localStorage.getItem(key)
      return v === null ? fallback : v === 'on'
    } catch {
      return fallback
    }
  })
  const set = useCallback(
    (next: boolean) => {
      setOn(next)
      try {
        window.localStorage.setItem(key, next ? 'on' : 'off')
      } catch {
        /* not remembered — fine */
      }
    },
    [key],
  )
  return [on, set]
}
